import React, { useState, useEffect, useRef, useMemo } from "react";
import { Save, Loader2, User, Briefcase, DollarSign, FileText, Calendar, AlertCircle, Shield, History, Plus, Folder, FolderPlus, Upload, ExternalLink, ChevronLeft, ChevronRight, FileImage, FileSpreadsheet, File as FileIcon, Search, ArrowRightLeft, X, FilePlus2 } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { cn } from "../utils";
import { UserAccessEditor } from "./UserAccessEditor";
import { PersonContractGenerationModal } from "./PersonContractGenerationModal";
import { canAccessPeopleHrRole } from "../services/peopleHrAccess";
import { stripPersonDriveFolderStatusMarker } from "../services/peopleDocuments";
import { buildPersonIntegrationSummary } from "../lib/personIntegrations";
import type { PersonContractHistoryRecord, PersonEmploymentRecord, PersonIntegrationEntry } from "../types";
import { SectionPillHeader } from "./SectionPillHeader";

interface EmployeeProfileProps {
  person: any;
  profile: any;
  onBack: () => void;
  onUpdate: () => void;
  onPreviousPerson?: (() => void) | null;
  onNextPerson?: (() => void) | null;
}

type Tab = "overview" | "employment" | "compensation" | "documents" | "dates" | "emergency" | "permissions" | "audit";
type PersonStatusValue = "active" | "inactive";

interface PersonDriveFile {
  id: string;
  name: string;
  mimeType: string;
  webViewLink?: string;
  iconLink?: string;
  modifiedTime?: string;
  createdTime?: string;
  size?: string;
}

interface PersonDocumentsResponse {
  rootFolderId: string | null;
  rootFolderName: string | null;
  currentFolderId: string | null;
  currentFolderName: string | null;
  breadcrumbs: Array<{ id: string; name: string }>;
  files: PersonDriveFile[];
  driveLinked?: boolean;
}

interface ContractGenerationBackgroundAttempt {
  attemptId: string;
  personName?: string;
  contractType?: string;
}

const EMPLOYMENT_CONTRACT_OPTIONS = [
  "Fixed Term",
  "Full Time",
  "Part Time",
  "Limited Company",
  "Contractor",
  "Freelance",
] as const;

interface CountryOption {
  code: string;
  name: string;
  flag: string;
  dialCode?: string;
}

const ISO_COUNTRY_CODES = [
  "AD","AE","AF","AG","AI","AL","AM","AO","AQ","AR","AS","AT","AU","AW","AX","AZ",
  "BA","BB","BD","BE","BF","BG","BH","BI","BJ","BL","BM","BN","BO","BQ","BR","BS","BT","BV","BW","BY","BZ",
  "CA","CC","CD","CF","CG","CH","CI","CK","CL","CM","CN","CO","CR","CU","CV","CW","CX","CY","CZ",
  "DE","DJ","DK","DM","DO","DZ",
  "EC","EE","EG","EH","ER","ES","ET",
  "FI","FJ","FK","FM","FO","FR",
  "GA","GB","GD","GE","GF","GG","GH","GI","GL","GM","GN","GP","GQ","GR","GS","GT","GU","GW","GY",
  "HK","HM","HN","HR","HT","HU",
  "ID","IE","IL","IM","IN","IO","IQ","IR","IS","IT",
  "JE","JM","JO","JP",
  "KE","KG","KH","KI","KM","KN","KP","KR","KW","KY","KZ",
  "LA","LB","LC","LI","LK","LR","LS","LT","LU","LV","LY",
  "MA","MC","MD","ME","MF","MG","MH","MK","ML","MM","MN","MO","MP","MQ","MR","MS","MT","MU","MV","MW","MX","MY","MZ",
  "NA","NC","NE","NF","NG","NI","NL","NO","NP","NR","NU","NZ",
  "OM",
  "PA","PE","PF","PG","PH","PK","PL","PM","PN","PR","PS","PT","PW","PY",
  "QA",
  "RE","RO","RS","RU","RW",
  "SA","SB","SC","SD","SE","SG","SH","SI","SJ","SK","SL","SM","SN","SO","SR","SS","ST","SV","SX","SY","SZ",
  "TC","TD","TF","TG","TH","TJ","TK","TL","TM","TN","TO","TR","TT","TV","TW","TZ",
  "UA","UG","UM","US","UY","UZ",
  "VA","VC","VE","VG","VI","VN","VU",
  "WF","WS",
  "XK",
  "YE","YT",
  "ZA","ZM","ZW"
] as const;

const COUNTRY_DIAL_CODE_MAP: Record<string, string> = {
  AE: "+971",
  AR: "+54",
  AT: "+43",
  AU: "+61",
  BE: "+32",
  BG: "+359",
  BR: "+55",
  CA: "+1",
  CH: "+41",
  CN: "+86",
  CY: "+357",
  CZ: "+420",
  DE: "+49",
  DK: "+45",
  EE: "+372",
  EG: "+20",
  ES: "+34",
  FI: "+358",
  FR: "+33",
  GB: "+44",
  GR: "+30",
  HK: "+852",
  HR: "+385",
  HU: "+36",
  IE: "+353",
  IL: "+972",
  IN: "+91",
  IS: "+354",
  IT: "+39",
  JP: "+81",
  KR: "+82",
  LT: "+370",
  LU: "+352",
  LV: "+371",
  MT: "+356",
  MX: "+52",
  MY: "+60",
  NL: "+31",
  NO: "+47",
  NZ: "+64",
  PH: "+63",
  PL: "+48",
  PT: "+351",
  RO: "+40",
  RS: "+381",
  SA: "+966",
  SE: "+46",
  SG: "+65",
  SI: "+386",
  SK: "+421",
  TH: "+66",
  TR: "+90",
  UA: "+380",
  US: "+1",
  ZA: "+27",
};

function flagEmojiFromCountryCode(code: string) {
  return code
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function buildCountryOptions(): CountryOption[] {
  const supportedValuesOf = (Intl as typeof Intl & {
    supportedValuesOf?: (key: string) => string[];
  }).supportedValuesOf;
  let regionCodes: readonly string[] = ISO_COUNTRY_CODES;
  if (typeof supportedValuesOf === "function") {
    try {
      const values = supportedValuesOf("region");
      if (Array.isArray(values) && values.length > 0) {
        regionCodes = values;
      }
    } catch {
      regionCodes = ISO_COUNTRY_CODES;
    }
  }
  const displayNames = typeof Intl.DisplayNames === "function"
    ? new Intl.DisplayNames(["en-GB"], { type: "region" })
    : null;

  const seen = new Set<string>();
  const countries = regionCodes
    .map((code) => code.toUpperCase())
    .filter((code) => /^[A-Z]{2}$/.test(code))
    .filter((code) => {
      if (seen.has(code)) return false;
      seen.add(code);
      return true;
    })
    .map((code) => {
      const name = displayNames?.of(code) || code;
      return {
        code,
        name,
        flag: flagEmojiFromCountryCode(code),
        dialCode: COUNTRY_DIAL_CODE_MAP[code],
      };
    })
    .filter((country) => country.name && country.name !== country.code)
    .sort((left, right) => left.name.localeCompare(right.name, "en-GB"));

  return countries;
}

const COUNTRY_OPTIONS: CountryOption[] = buildCountryOptions();
const PHONE_COUNTRY_OPTIONS = COUNTRY_OPTIONS.filter((country) => Boolean(country.dialCode));

function splitDisplayName(value?: string | null) {
  const normalized = String(value || "").trim();
  if (!normalized) return { firstName: "", lastName: "" };
  const parts = normalized.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function getFirstFilledProfileValue<T = string>(...values: Array<T | null | undefined | "">) {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return undefined;
}

function normalizeDialCode(value?: string | null) {
  const trimmed = String(value || "").trim();
  if (!trimmed.startsWith("+")) return "";
  return trimmed.replace(/[^\d+]/g, "");
}

function findCountryOptionByName(name?: string | null) {
  const normalized = String(name || "").trim().toLowerCase();
  if (!normalized) return null;
  return COUNTRY_OPTIONS.find((country) => country.name.toLowerCase() === normalized) || null;
}

function detectCountryFromPhoneNumber(phoneNumber?: string | null) {
  const normalized = normalizeDialCode(phoneNumber);
  if (!normalized) return null;
  const matches = PHONE_COUNTRY_OPTIONS.filter((country) => country.dialCode && normalized.startsWith(country.dialCode)).sort(
    (left, right) => right.dialCode.length - left.dialCode.length,
  );
  return matches[0] || null;
}

function getPhoneLocalNumber(phoneNumber: string, countryDialCode?: string) {
  const trimmed = String(phoneNumber || "").trim();
  if (!trimmed) return "";
  const dialCode = normalizeDialCode(countryDialCode);
  if (dialCode && trimmed.startsWith(dialCode)) {
    return trimmed.slice(dialCode.length).trim();
  }
  return trimmed.replace(/^\+\d+\s*/, "").trim();
}

function buildInternationalPhoneNumber(dialCode: string, localNumber: string) {
  const sanitizedLocal = String(localNumber || "").replace(/[^\d()\-\s]/g, "").trim();
  if (!sanitizedLocal) return "";
  const normalizedDialCode = normalizeDialCode(dialCode);
  return normalizedDialCode ? `${normalizedDialCode} ${sanitizedLocal}`.trim() : sanitizedLocal;
}

function getDisplayDriveFolderName(name?: string | null, fallback?: string | null) {
  return stripPersonDriveFolderStatusMarker(String(name || "").trim()) || String(fallback || "").trim() || "Person Root";
}

function normalizeEmploymentContractType(value?: string) {
  if (!value) return "";
  const normalized = String(value).trim().toLowerCase();
  if (normalized === "full-time" || normalized === "full time") return "Full Time";
  if (normalized === "part-time" || normalized === "part time") return "Part Time";
  if (normalized === "fixed-term" || normalized === "fixed term") return "Fixed Term";
  if (normalized === "limited-company" || normalized === "limited company") return "Limited Company";
  if (normalized === "contractor") return "Contractor";
  if (normalized === "freelance") return "Freelance";
  return value;
}

function buildProfileSavePayload(profile: any, personStatus: PersonStatusValue) {
  const normalizedFirstName = String(profile.firstName || "").trim();
  const normalizedLastName = String(profile.lastName || "").trim();
  const normalizedFullName = [normalizedFirstName, normalizedLastName].filter(Boolean).join(" ").trim();
  return {
    ...profile,
    firstName: normalizedFirstName,
    lastName: normalizedLastName,
    fullName: normalizedFullName,
    name: normalizedFullName,
    active: personStatus === "active",
  };
}

function buildProfileDirtySignature(payload: any) {
  return JSON.stringify(payload);
}

export function EmployeeProfile({
  person,
  profile: initialProfile,
  onBack,
  onUpdate,
  onPreviousPerson,
  onNextPerson,
}: EmployeeProfileProps) {
  const { user, effectiveProfile } = useAuth();
  const initialNameParts = splitDisplayName(person.name || person.fullName);
  const initialCountryOption =
    findCountryOptionByName(initialProfile?.country || person.country) ||
    detectCountryFromPhoneNumber(initialProfile?.personalPhone || initialProfile?.phone || person.phone) ||
    COUNTRY_OPTIONS.find((country) => country.code === "GB") ||
    COUNTRY_OPTIONS[0];
  const [activeTab, setActiveTab] = useState<Tab>("overview");
  const [profile, setProfile] = useState<any>(() => ({
    id: person.id,
    personId: person.id,
    ...(initialProfile || {}),
    email: initialProfile?.email ?? person.email ?? "",
    firstName: initialProfile?.firstName ?? person.firstName ?? initialNameParts.firstName,
    lastName: initialProfile?.lastName ?? person.lastName ?? initialNameParts.lastName,
    dailyRate: initialProfile?.dailyRate ?? person.dailyRate ?? undefined,
  }));
  const [selectedPhoneCountryCode, setSelectedPhoneCountryCode] = useState<string>(initialCountryOption?.code || "GB");
  const [countryQuery, setCountryQuery] = useState<string>(
    initialCountryOption?.name || initialProfile?.country || person.country || "",
  );
  const [isCountryMenuOpen, setIsCountryMenuOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [personStatus, setPersonStatus] = useState<PersonStatusValue>(person.active === false ? "inactive" : "active");
  const [personPhotoUrl, setPersonPhotoUrl] = useState(person.photoUrl || "");
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [pushingPhotoToWorkspace, setPushingPhotoToWorkspace] = useState(false);

  const [documents, setDocuments] = useState<PersonDriveFile[]>([]);
  const [documentsState, setDocumentsState] = useState<PersonDocumentsResponse | null>(null);
  const [contractHistory, setContractHistory] = useState<PersonContractHistoryRecord[]>([]);
  const [employmentRecords, setEmploymentRecords] = useState<PersonEmploymentRecord[]>([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingContractHistory, setLoadingContractHistory] = useState(false);
  const [loadingEmploymentRecords, setLoadingEmploymentRecords] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [creatingSubfolder, setCreatingSubfolder] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [documentDropActive, setDocumentDropActive] = useState(false);
  const [showContractGenerator, setShowContractGenerator] = useState(false);
  const [linkingExistingFolder, setLinkingExistingFolder] = useState(false);
  const [documentSearch, setDocumentSearch] = useState("");
  const [movingDocument, setMovingDocument] = useState<PersonDriveFile | null>(null);
  const [moveTargetState, setMoveTargetState] = useState<PersonDocumentsResponse | null>(null);
  const [moveTargetLoading, setMoveTargetLoading] = useState(false);
  const [submittingMove, setSubmittingMove] = useState(false);
  const [movingContractFolders, setMovingContractFolders] = useState(false);
  const [creatingManagementFolders, setCreatingManagementFolders] = useState(false);
  const [backgroundContractAttempt, setBackgroundContractAttempt] = useState<ContractGenerationBackgroundAttempt | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const countryComboboxRef = useRef<HTMLDivElement | null>(null);
  const documentDragDepthRef = useRef(0);
  const lastSavedSignatureRef = useRef("");
  const autosaveInFlightRef = useRef(false);
  const canAccessHrDocuments = canAccessPeopleHrRole(effectiveProfile?.role);
  const integrationStatus = person.integrationStatus || buildPersonIntegrationSummary(person);
  const integrationEntries = Object.values(integrationStatus) as PersonIntegrationEntry[];
  const filteredDocuments = documents.filter((doc) =>
    doc.name.toLowerCase().includes(documentSearch.trim().toLowerCase())
  );
  const profilePhone = String(getFirstFilledProfileValue(profile.personalPhone, profile.phone) || "");
  const profileWorkEmail = String(getFirstFilledProfileValue(profile.email, person.email) || "");
  const profilePersonalEmail = String(getFirstFilledProfileValue(profile.personalEmail) || "");
  const profileFirstName = String(getFirstFilledProfileValue(profile.firstName, person.firstName, initialNameParts.firstName) || "");
  const profileLastName = String(getFirstFilledProfileValue(profile.lastName, person.lastName, initialNameParts.lastName) || "");
  const profileDisplayName = [profileFirstName, profileLastName].filter(Boolean).join(" ").trim() || person.name || person.fullName || "User";
  const profileAddressLine1 = String(getFirstFilledProfileValue(profile.address) || "");
  const profileAddressLine2 = String(getFirstFilledProfileValue(profile.addressLine2) || "");
  const profileCity = String(getFirstFilledProfileValue(profile.city) || "");
  const profileStateProvince = String(getFirstFilledProfileValue(profile.stateProvince) || "");
  const profilePostalCode = String(getFirstFilledProfileValue(profile.postalCode) || "");
  const profileCountry = String(getFirstFilledProfileValue(profile.country) || "");
  const selectedPhoneCountry =
    PHONE_COUNTRY_OPTIONS.find((country) => country.code === selectedPhoneCountryCode) ||
    PHONE_COUNTRY_OPTIONS.find((country) => country.code === "GB") ||
    PHONE_COUNTRY_OPTIONS[0];
  const profilePhoneLocalNumber = getPhoneLocalNumber(profilePhone, selectedPhoneCountry?.dialCode);
  const profileContractType = normalizeEmploymentContractType(String(getFirstFilledProfileValue(profile.contractType) || ""));
  const profileLeaveAllowance = getFirstFilledProfileValue<number | string>(
    profile.annualLeaveAllowance,
    profile.leaveAllowance,
  );

  const selectedCountryOption = findCountryOptionByName(profileCountry);
  const currentSavePayload = useMemo(
    () => buildProfileSavePayload(profile, personStatus),
    [personStatus, profile],
  );
  const isDirty = useMemo(
    () => buildProfileDirtySignature(currentSavePayload) !== lastSavedSignatureRef.current,
    [currentSavePayload],
  );
  const filteredCountryOptions = useMemo(() => {
    const normalizedQuery = countryQuery.trim().toLowerCase();
    if (!normalizedQuery) {
      return COUNTRY_OPTIONS;
    }

    return [...COUNTRY_OPTIONS].sort((left, right) => {
      const leftName = left.name.toLowerCase();
      const rightName = right.name.toLowerCase();
      const leftStarts = leftName.startsWith(normalizedQuery) ? 0 : leftName.includes(normalizedQuery) ? 1 : 2;
      const rightStarts = rightName.startsWith(normalizedQuery) ? 0 : rightName.includes(normalizedQuery) ? 1 : 2;
      if (leftStarts !== rightStarts) return leftStarts - rightStarts;
      return left.name.localeCompare(right.name, "en-GB");
    }).filter((country) => country.name.toLowerCase().includes(normalizedQuery));
  }, [countryQuery]);

  const commitCountrySelection = (country: CountryOption | null) => {
    setProfile((current: any) => ({
      ...current,
      country: country?.name || "",
      personalPhone: country?.dialCode
        ? buildInternationalPhoneNumber(
            country.dialCode,
            getPhoneLocalNumber(current.personalPhone || current.phone || "", selectedPhoneCountry?.dialCode || country.dialCode),
          )
        : current.personalPhone,
    }));
    setCountryQuery(country?.name || "");
    if (country?.dialCode) {
      setSelectedPhoneCountryCode(country.code);
    }
    setIsCountryMenuOpen(false);
  };

  useEffect(() => {
    if (activeTab === "documents" && canAccessHrDocuments) {
      fetchDocuments();
      fetchContractHistory();
    }
    if (activeTab === "employment") {
      fetchEmploymentRecords();
    }
  }, [activeTab, profile.driveFolderId, canAccessHrDocuments]);

  useEffect(() => {
    const nextNameParts = splitDisplayName(person.name || person.fullName);
    const nextCountryOption =
      findCountryOptionByName(initialProfile?.country || person.country) ||
      detectCountryFromPhoneNumber(initialProfile?.personalPhone || initialProfile?.phone || person.phone) ||
      COUNTRY_OPTIONS.find((country) => country.code === "GB") ||
      COUNTRY_OPTIONS[0];
    setProfile({
      id: person.id,
      personId: person.id,
      ...(initialProfile || {}),
      email: initialProfile?.email ?? person.email ?? "",
      firstName: initialProfile?.firstName ?? person.firstName ?? nextNameParts.firstName,
      lastName: initialProfile?.lastName ?? person.lastName ?? nextNameParts.lastName,
      dailyRate: initialProfile?.dailyRate ?? person.dailyRate ?? undefined,
    });
    setSelectedPhoneCountryCode(nextCountryOption?.code || "GB");
    setCountryQuery(nextCountryOption?.name || initialProfile?.country || person.country || "");
    lastSavedSignatureRef.current = buildProfileDirtySignature(
      buildProfileSavePayload(
        {
          id: person.id,
          personId: person.id,
          ...(initialProfile || {}),
          email: initialProfile?.email ?? person.email ?? "",
          firstName: initialProfile?.firstName ?? person.firstName ?? nextNameParts.firstName,
          lastName: initialProfile?.lastName ?? person.lastName ?? nextNameParts.lastName,
          dailyRate: initialProfile?.dailyRate ?? person.dailyRate ?? undefined,
        },
        person.active === false ? "inactive" : "active",
      ),
    );
  }, [initialProfile, person.id, person.dailyRate]);

  useEffect(() => {
    const persistDraftOnUnload = () => {
      if (!user || !isDirty || autosaveInFlightRef.current) return;
      autosaveInFlightRef.current = true;
      void fetch(`/api/people/profiles/${person.id}`, {
        method: "PUT",
        keepalive: true,
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: JSON.stringify(currentSavePayload),
      }).catch(() => {
        autosaveInFlightRef.current = false;
      });
    };

    window.addEventListener("pagehide", persistDraftOnUnload);
    return () => {
      window.removeEventListener("pagehide", persistDraftOnUnload);
    };
  }, [currentSavePayload, isDirty, person.id, user]);

  useEffect(() => {
    const handlePointerDown = (event: MouseEvent) => {
      if (!countryComboboxRef.current?.contains(event.target as Node)) {
        setIsCountryMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, []);

  useEffect(() => {
    setPersonPhotoUrl(person.photoUrl || "");
  }, [person.photoUrl, person.id]);

  useEffect(() => {
    setPersonStatus(person.active === false ? "inactive" : "active");
  }, [person.active, person.id]);

  useEffect(() => {
    if (!user || !backgroundContractAttempt) return;

    let cancelled = false;
    const pollAttempt = async () => {
      try {
        const response = await fetch(
          `/api/people/profiles/${person.id}/contracts/attempts/${backgroundContractAttempt.attemptId}`,
          {
            headers: {
              "x-user-uid": user.uid,
              "x-user-email": user.email || "",
            },
          },
        );
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(data.error || "Failed to load contract generation status.");
        }
        if (cancelled) return;

        if (data.status === "queued" || data.status === "started") {
          window.setTimeout(pollAttempt, 2500);
          return;
        }

        setBackgroundContractAttempt(null);
        await onUpdate();
        setDocumentsState(null);
        await Promise.all([fetchDocuments(), fetchContractHistory(), fetchEmploymentRecords()]);

        const attemptLabel = [backgroundContractAttempt.personName || person.name, backgroundContractAttempt.contractType]
          .filter(Boolean)
          .join(" • ");

        if (data.status === "success") {
          toast.success(`Contract ready${attemptLabel ? `: ${attemptLabel}` : ""}`);
        } else if (data.status === "partial_failure") {
          toast.warning(`Contract completed with warnings${attemptLabel ? `: ${attemptLabel}` : ""}`);
        } else {
          toast.error(data.errorMessage || `Contract generation failed${attemptLabel ? `: ${attemptLabel}` : ""}`);
        }
      } catch (error: any) {
        if (cancelled) return;
        setBackgroundContractAttempt(null);
        toast.error(error.message || "Failed to track contract generation.");
      }
    };

    pollAttempt();
    return () => {
      cancelled = true;
    };
  }, [backgroundContractAttempt, person.id, person.name, user]);

  const fetchDocumentsView = async (folderId?: string, options?: { silent?: boolean }) => {
    if (!user || !canAccessHrDocuments) return null;
    if (!options?.silent) {
      setLoadingDocs(true);
    }
    try {
      const targetFolderId = folderId || documentsState?.currentFolderId || profile.driveFolderId;
      const query = targetFolderId ? `?folderId=${encodeURIComponent(targetFolderId)}` : "";
      const res = await fetch(`/api/people/profiles/${person.id}/documents${query}`, {
        headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" }
      });
      if (!res.ok) throw new Error("Failed to fetch documents");
      const data = (await res.json()) as PersonDocumentsResponse;
      return data;
    } catch (error: any) {
      toast.error(error.message);
      return null;
    } finally {
      if (!options?.silent) {
        setLoadingDocs(false);
      }
    }
  };

  const fetchDocuments = async (folderId?: string) => {
    const data = await fetchDocumentsView(folderId);
    if (!data) return;
    setDocumentsState(data);
    setDocuments(data.files || []);
    setProfile((current: any) => ({
      ...current,
      driveFolderId: data.rootFolderId || current.driveFolderId,
      driveFolderName: data.rootFolderName || current.driveFolderName,
      driveLinked: Boolean(data.driveLinked ?? current.driveLinked ?? data.rootFolderId),
    }));
  };

  const fetchContractHistory = async () => {
    if (!user || !canAccessHrDocuments) return;
    setLoadingContractHistory(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/contracts/history`, {
        headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" },
      });
      if (!res.ok) throw new Error("Failed to fetch contract history");
      const data = (await res.json()) as PersonContractHistoryRecord[];
      setContractHistory(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error(error.message || "Failed to load contract history");
    } finally {
      setLoadingContractHistory(false);
    }
  };

  const fetchEmploymentRecords = async () => {
    if (!user) return;
    setLoadingEmploymentRecords(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/employment-records`, {
        headers: { "x-user-uid": user.uid, "x-user-email": user.email || "" },
      });
      if (!res.ok) throw new Error("Failed to fetch employment records");
      const data = (await res.json()) as PersonEmploymentRecord[];
      setEmploymentRecords(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error(error.message || "Failed to load employment records");
    } finally {
      setLoadingEmploymentRecords(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!user) return;
    setCreatingFolder(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/folder`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || ""
        },
        body: JSON.stringify({ name: person.name })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to create folder");

      setProfile((current: any) => ({
        ...current,
        driveFolderId: data.folderId,
        driveFolderName: data.folderName || current.driveFolderName || person.name,
        driveLinked: true,
        driveLastSyncedAt: data.driveLastSyncedAt || new Date().toISOString(),
      }));
      toast.success("Drive folder linked");
      onUpdate();
      setActiveTab("documents");
      setDocumentsState(null);
      await fetchDocuments();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleOpenFolder = async (folderId: string) => {
    if (!folderId) return;
    await fetchDocuments(folderId);
  };

  const handleLinkExistingFolder = async () => {
    if (!user) return;
    setLinkingExistingFolder(true);
    try {
      const linkFolder = async (confirmLinkExisting: boolean) =>
        fetch(`/api/people/profiles/${person.id}/folder/link-existing`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-user-uid": user.uid,
            "x-user-email": user.email || "",
          },
          body: JSON.stringify({ confirmLinkExisting }),
        });

      let res = await linkFolder(false);
      let data = await res.json().catch(() => ({}));
      if (res.status === 409 && data?.requiresConfirmation) {
        const confirmed = window.confirm(
          data.error || `Found an existing folder${data.folderName ? `: ${data.folderName}` : ""}. Do you want to link it to this person?`
        );
        if (!confirmed) {
          return;
        }
        res = await linkFolder(true);
        data = await res.json().catch(() => ({}));
      }

      if (!res.ok) throw new Error(data.error || "Failed to link existing folder");
      setProfile((current: any) => ({
        ...current,
        driveFolderId: data.folderId,
        driveFolderName: data.folderName || current.driveFolderName || person.name,
        driveLinked: true,
        driveLastSyncedAt: data.driveLastSyncedAt || new Date().toISOString(),
      }));
      setDocumentsState(null);
      toast.success("Drive folder linked");
      onUpdate();
      await fetchDocuments();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLinkingExistingFolder(false);
    }
  };

  const handleCreateSubfolder = async () => {
    if (!user || !documentsState?.currentFolderId) return;
    const name = window.prompt("New folder name");
    if (!name || !name.trim()) return;
    setCreatingSubfolder(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/documents/folders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || ""
        },
        body: JSON.stringify({
          folderId: documentsState.currentFolderId,
          name: name.trim(),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to create folder");
      toast.success(data.created ? "Folder created" : "Using existing folder");
      await fetchDocuments();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setCreatingSubfolder(false);
    }
  };

  const uploadDocumentToCurrentFolder = async (file?: globalThis.File | null) => {
    if (!user || !documentsState?.currentFolderId || !file) return;
    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folderId", documentsState.currentFolderId);
      const res = await fetch(`/api/people/profiles/${person.id}/documents/upload`, {
        method: "POST",
        headers: {
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to upload file");
      toast.success("File uploaded");
      await fetchDocuments();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUploadingDocument(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUploadDocument = async (file?: globalThis.File | null) => {
    await uploadDocumentToCurrentFolder(file);
  };

  const handleDocumentDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
    if (!documentsState?.currentFolderId || uploadingDocument) return;
    event.preventDefault();
    event.stopPropagation();
    documentDragDepthRef.current += 1;
    setDocumentDropActive(true);
  };

  const handleDocumentDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    if (!documentsState?.currentFolderId || uploadingDocument) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = "copy";
  };

  const handleDocumentDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!documentsState?.currentFolderId || uploadingDocument) return;
    event.preventDefault();
    event.stopPropagation();
    documentDragDepthRef.current = Math.max(0, documentDragDepthRef.current - 1);
    if (documentDragDepthRef.current === 0) {
      setDocumentDropActive(false);
    }
  };

  const handleDocumentDrop = async (event: React.DragEvent<HTMLDivElement>) => {
    if (!documentsState?.currentFolderId || uploadingDocument) return;
    event.preventDefault();
    event.stopPropagation();
    documentDragDepthRef.current = 0;
    setDocumentDropActive(false);
    const file = event.dataTransfer.files?.[0];
    if (!file) return;
    await uploadDocumentToCurrentFolder(file);
  };

  const openMoveDialog = async (doc: PersonDriveFile) => {
    setMovingDocument(doc);
    setMoveTargetLoading(true);
    try {
      const data = await fetchDocumentsView(doc.mimeType === "application/vnd.google-apps.folder" ? documentsState?.rootFolderId || profile.driveFolderId : documentsState?.currentFolderId || profile.driveFolderId, { silent: true });
      if (!data) {
        setMovingDocument(null);
        return;
      }
      setMoveTargetState(data);
    } finally {
      setMoveTargetLoading(false);
    }
  };

  const handleOpenMoveTargetFolder = async (folderId: string) => {
    if (!folderId) return;
    setMoveTargetLoading(true);
    try {
      const data = await fetchDocumentsView(folderId, { silent: true });
      if (data) {
        setMoveTargetState(data);
      }
    } finally {
      setMoveTargetLoading(false);
    }
  };

  const closeMoveDialog = () => {
    setMovingDocument(null);
    setMoveTargetState(null);
    setMoveTargetLoading(false);
    setSubmittingMove(false);
  };

  const handleMoveDocument = async () => {
    if (!user || !movingDocument || !moveTargetState?.currentFolderId) return;
    setSubmittingMove(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/documents/move`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: JSON.stringify({
          fileId: movingDocument.id,
          targetFolderId: moveTargetState.currentFolderId,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to move item");
      toast.success(data.unchanged ? "Item is already in that folder" : "Item moved");
      closeMoveDialog();
      await fetchDocuments(documentsState?.currentFolderId || profile.driveFolderId);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setSubmittingMove(false);
    }
  };

  const handleMoveContractFolders = async () => {
    if (!user) return;
    const confirmed = window.confirm("Create or reuse a Contracts folder and move all contract folders into it?");
    if (!confirmed) return;

    setMovingContractFolders(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/documents/move-contract-folders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to move contract folders");
      if (data.movedCount > 0) {
        toast.success(`Moved ${data.movedCount} contract folder${data.movedCount === 1 ? "" : "s"} to Contracts`);
      } else {
        toast.success("No contract folders needed moving");
      }
      await fetchDocuments(documentsState?.currentFolderId || profile.driveFolderId);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setMovingContractFolders(false);
    }
  };

  const handleCreateManagementFolders = async () => {
    if (!user || !profile.driveFolderId) return;
    setCreatingManagementFolders(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/documents/management-folders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to create management folders");
      toast.success("Management folders created");
      await fetchDocuments(documentsState?.currentFolderId || profile.driveFolderId);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setCreatingManagementFolders(false);
    }
  };

  const getDocumentIcon = (doc: PersonDriveFile) => {
    if (doc.mimeType === "application/vnd.google-apps.folder") {
      return <Folder className="w-4 h-4 text-amber-500" />;
    }
    if (doc.mimeType?.includes("spreadsheet")) {
      return <FileSpreadsheet className="w-4 h-4 text-emerald-600" />;
    }
    if (doc.mimeType?.startsWith("image/")) {
      return <FileImage className="w-4 h-4 text-indigo-500" />;
    }
    return <FileIcon className="w-4 h-4 text-slate-400" />;
  };

  const persistProfile = async ({ silent = false }: { silent?: boolean } = {}) => {
    if (!user) return false;
    if (!isDirty && !silent) return true;
    if (!silent) {
      setSaving(true);
    }
    autosaveInFlightRef.current = true;
    try {
      const res = await fetch(`/api/people/profiles/${person.id}`, {
        method: "PUT",
        headers: { 
          "Content-Type": "application/json",
          "x-user-uid": user.uid,
          "x-user-email": user.email || "" 
        },
        body: JSON.stringify(currentSavePayload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to save profile");
      lastSavedSignatureRef.current = buildProfileDirtySignature(currentSavePayload);
      if (!silent) {
        toast.success("Profile saved successfully");
      }
      await onUpdate();
      if (activeTab === "employment" || activeTab === "compensation" || activeTab === "dates") {
        await fetchEmploymentRecords();
      }
      return true;
    } catch (error: any) {
      if (!silent) {
        toast.error(error.message);
      }
      return false;
    } finally {
      autosaveInFlightRef.current = false;
      if (!silent) {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    await persistProfile();
  };

  const saveBeforeNavigate = async (navigate: () => void) => {
    if (saving) return;
    if (!isDirty) {
      navigate();
      return;
    }
    const saved = await persistProfile({ silent: true });
    if (!saved) {
      toast.error("Couldn’t auto-save before navigating away. Please try Save Changes.");
      return;
    }
    navigate();
  };

  const handleBackNavigation = async () => {
    await saveBeforeNavigate(onBack);
  };

  const handleSwitchTab = async (nextTab: Tab) => {
    if (nextTab === activeTab) return;
    await saveBeforeNavigate(() => setActiveTab(nextTab));
  };

  const handlePreviousPerson = async () => {
    if (!onPreviousPerson) return;
    await saveBeforeNavigate(onPreviousPerson);
  };

  const handleNextPerson = async () => {
    if (!onNextPerson) return;
    await saveBeforeNavigate(onNextPerson);
  };

  const handleSaveAccess = async (updates: { role: any; status: any; customPermissions: string[] }) => {
    if (!user) throw new Error("You must be signed in");
    const res = await fetch(`/api/users/${person.id}/access`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "x-user-uid": user.uid
      },
      body: JSON.stringify(updates)
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.error || "Failed to update user access");
    }
    onUpdate();
  };

  const handleUploadPhoto = async (file?: globalThis.File | null) => {
    if (!user || !file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append("photo", file);
      const res = await fetch(`/api/people/profiles/${person.id}/photo`, {
        method: "POST",
        headers: {
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
        body: formData,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to upload profile photo");
      setPersonPhotoUrl(data.photoUrl || "");
      toast.success("Profile photo updated");
      onUpdate();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setUploadingPhoto(false);
      if (photoInputRef.current) photoInputRef.current.value = "";
    }
  };

  const handlePushPhotoToWorkspace = async () => {
    if (!user) return;
    setPushingPhotoToWorkspace(true);
    try {
      const res = await fetch(`/api/people/profiles/${person.id}/photo/push-workspace`, {
        method: "POST",
        headers: {
          "x-user-uid": user.uid,
          "x-user-email": user.email || "",
        },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || "Failed to push photo to Google Workspace");
      toast.success("Profile photo pushed to Google Workspace");
      onUpdate();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setPushingPhotoToWorkspace(false);
    }
  };

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "employment", label: "Employment", icon: Briefcase },
    { id: "compensation", label: "Compensation", icon: DollarSign },
    ...(canAccessHrDocuments ? [{ id: "documents", label: "Documents", icon: FileText }] : []),
    { id: "dates", label: "Dates & Reviews", icon: Calendar },
    { id: "emergency", label: "Emergency", icon: AlertCircle },
    { id: "permissions", label: "Permissions", icon: Shield },
    { id: "audit", label: "Audit", icon: History },
  ] as Array<{ id: Tab; label: string; icon: any }>;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <PersonContractGenerationModal
        isOpen={showContractGenerator}
        onClose={() => setShowContractGenerator(false)}
        onQueued={(attempt) => {
          setBackgroundContractAttempt(attempt);
          setShowContractGenerator(false);
        }}
        onGenerated={async () => {
          onUpdate();
          setActiveTab("documents");
          setDocumentsState(null);
          await Promise.all([fetchDocuments(), fetchContractHistory()]);
        }}
        onViewDocuments={() => {
          setActiveTab("documents");
          setDocumentsState(null);
          fetchDocuments();
          fetchContractHistory();
        }}
        person={person}
        profile={profile}
      />
      {movingDocument && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-sm">
          <div className="w-full max-w-3xl rounded-2xl border border-slate-200 bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">Move Item</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Choose a destination folder inside this person&apos;s documents area for <span className="font-medium text-slate-700">{movingDocument.name}</span>.
                </p>
              </div>
              <button
                type="button"
                onClick={closeMoveDialog}
                className="rounded-lg border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div className="rounded-xl border border-slate-200 overflow-hidden min-h-[320px] flex flex-col">
                <div className="px-3 py-1.5 bg-slate-50/50 border-b border-slate-100 flex items-center gap-2 overflow-x-auto text-[11px] font-medium text-slate-500">
                  <button
                    type="button"
                    onClick={() => moveTargetState?.rootFolderId && handleOpenMoveTargetFolder(moveTargetState.rootFolderId)}
                    className="hover:text-indigo-600 transition-colors flex items-center gap-1.5 shrink-0"
                  >
                    <Folder className="w-3.5 h-3.5" />
                    {getDisplayDriveFolderName(moveTargetState?.rootFolderName || profile.driveFolderName, "Person Root")}
                  </button>
                  {(moveTargetState?.breadcrumbs || [])
                    .filter((crumb) => crumb.id !== moveTargetState?.rootFolderId)
                    .map((crumb) => (
                      <React.Fragment key={crumb.id}>
                        <ChevronRight className="w-3 h-3 text-slate-400" />
                        <button
                          type="button"
                          onClick={() => handleOpenMoveTargetFolder(crumb.id)}
                          className={cn(
                            "hover:text-indigo-600 transition-colors truncate max-w-[280px] flex items-center gap-1.5 shrink-0",
                            moveTargetState?.currentFolderId === crumb.id ? "text-slate-800" : "text-slate-500"
                          )}
                        >
                          <Folder className="w-3 h-3" />
                          {crumb.name}
                        </button>
                      </React.Fragment>
                    ))}
                </div>

                <div className="flex-1 overflow-auto">
                  {moveTargetLoading ? (
                    <div className="h-full flex items-center justify-center p-12">
                      <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {moveTargetState?.files.filter((doc) => doc.mimeType === "application/vnd.google-apps.folder").map((doc) => (
                        <div key={doc.id} className="flex items-center justify-between p-3 hover:bg-slate-50 transition-colors">
                          <button
                            type="button"
                            onClick={() => handleOpenMoveTargetFolder(doc.id)}
                            className="flex min-w-0 items-center gap-3 text-left transition-colors hover:text-indigo-600"
                          >
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded bg-slate-100">
                              {getDocumentIcon(doc)}
                            </div>
                            <div className="min-w-0">
                              <div className="truncate text-sm font-medium text-slate-900">{doc.name}</div>
                              <div className="text-xs text-slate-500">
                                Modified {doc.modifiedTime ? new Date(doc.modifiedTime).toLocaleDateString() : "—"}
                              </div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleOpenMoveTargetFolder(doc.id)}
                            className="text-[11px] font-medium text-slate-500 hover:text-indigo-600 transition-colors"
                          >
                            Open
                          </button>
                        </div>
                      ))}
                      {!moveTargetLoading && (moveTargetState?.files.filter((doc) => doc.mimeType === "application/vnd.google-apps.folder").length || 0) === 0 && (
                        <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                          <div className="w-12 h-12 bg-slate-50 rounded-full flex items-center justify-center mb-3">
                            <Folder className="w-6 h-6 text-slate-300" />
                          </div>
                          <p className="text-xs text-slate-500">No subfolders here yet.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between gap-3">
                <p className="text-sm text-slate-500">
                  Destination: <span className="font-medium text-slate-700">{getDisplayDriveFolderName(moveTargetState?.currentFolderName || moveTargetState?.rootFolderName, "Person Root")}</span>
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={closeMoveDialog}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleMoveDocument}
                    disabled={submittingMove || moveTargetLoading || !moveTargetState?.currentFolderId}
                    className="inline-flex items-center gap-2 rounded-lg border border-[#ffcc01] bg-[#ffcc01] px-3 py-2 text-sm font-medium text-slate-900 transition-colors hover:brightness-95 disabled:opacity-50"
                  >
                    {submittingMove ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                    Move Here
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="mb-4 rounded-lg border border-slate-200 bg-white px-6 py-5 shadow-sm dark:border-slate-800 dark:bg-slate-900">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            <img 
              src={personPhotoUrl || `https://ui-avatars.com/api/?name=${encodeURIComponent(profileDisplayName)}&background=f1f5f9&color=64748b`} 
              alt={profileDisplayName}
              className="h-[60px] w-[60px] rounded-md object-cover"
              referrerPolicy="no-referrer"
            />
            <div className="min-w-0">
              <h2 className="truncate text-xl font-brandon font-medium leading-tight text-slate-900 dark:text-slate-100">{profileDisplayName}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-slate-500 dark:text-slate-400">
                <span>{person.title || "No title"}</span>
                <div className="h-4 w-px border-l border-slate-200 dark:border-slate-700" />
                <span>{person.department || "No department"}</span>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={handlePreviousPerson}
              disabled={!onPreviousPerson}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              aria-label="Previous person"
              title="Previous person"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleNextPerson}
              disabled={!onNextPerson}
              className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-300 text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              aria-label="Next person"
              title="Next person"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          {canAccessHrDocuments && (
            <>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(event) => handleUploadPhoto(event.target.files?.[0] || null)}
              />
              <button
                type="button"
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                Upload Photo
              </button>
              {person.isWorkspaceUser && (
                <button
                  type="button"
                  onClick={handlePushPhotoToWorkspace}
                  disabled={pushingPhotoToWorkspace || !personPhotoUrl}
                  className="flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                >
                  {pushingPhotoToWorkspace ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Push to Workspace
                </button>
              )}
            </>
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-[#ffcc01] px-3 py-1.5 text-xs font-medium text-slate-900 transition-colors hover:bg-[#e6b801] disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save Changes
          </button>
        </div>
      </div>
      </div>

      <div className="mb-4 shrink-0 overflow-x-auto border-b border-slate-200 dark:border-slate-800">
        <nav className="-mb-px flex min-w-max space-x-8 pr-4" aria-label="Tabs">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleSwitchTab(tab.id as Tab)}
                className={cn(
                  "flex items-center gap-[3px] whitespace-nowrap rounded-none border-b-2 bg-transparent px-1 py-3 text-sm font-medium transition-colors outline-none focus:outline-none focus-visible:outline-none focus-visible:ring-0",
                  activeTab === tab.id
                    ? "border-[#ffcc01] text-[#ffcc01] dark:text-[#ffcc01]"
                    : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700 dark:text-slate-400 dark:hover:border-slate-600 dark:hover:text-slate-200"
                )}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className={cn(
        activeTab === "documents"
          ? "space-y-6"
          : "rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900"
      )}>
          {activeTab === "overview" && (
            <div className="space-y-5">
              <div className="flex flex-col gap-3 border-b border-slate-200 pb-3 dark:border-slate-800 sm:flex-row sm:items-start sm:justify-between">
                <SectionPillHeader
                  icon={User}
                  title="Overview"
                  description="Keep the core staff record clean and complete. Name changes here feed back into the app’s main people directory."
                />
                <div className="min-w-[180px] sm:max-w-[220px]">
                  <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
                    Person Status
                  </label>
                  <select
                    value={personStatus}
                    onChange={(event) => setPersonStatus(event.target.value as PersonStatusValue)}
                    className={cn(
                      "w-full rounded-xl px-3 py-2.5 text-sm font-semibold outline-none transition focus:ring-2 focus:ring-[#ffcc01]/30",
                      personStatus === "active"
                        ? "border border-emerald-200 bg-emerald-50 text-emerald-900 focus:border-emerald-300 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200"
                        : "border border-rose-200 bg-rose-50 text-rose-900 focus:border-rose-300 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-200"
                    )}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(340px,420px)] xl:items-start">
                <div className="space-y-5">
                  <section className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5 dark:border-slate-800 dark:bg-slate-900/50">
                    <div className="mb-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Identity</p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Core directory details used throughout the app.</p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">First Name</label>
                        <input
                          type="text"
                          value={profileFirstName}
                          onChange={e => setProfile({ ...profile, firstName: e.target.value })}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Surname</label>
                        <input
                          type="text"
                          value={profileLastName}
                          onChange={e => setProfile({ ...profile, lastName: e.target.value })}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Work Email</label>
                        <input
                          type="email"
                          value={profileWorkEmail}
                          onChange={e => setProfile({ ...profile, email: e.target.value })}
                          placeholder="name@vinefx.com"
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Personal Email</label>
                        <input
                          type="email"
                          value={profilePersonalEmail}
                          onChange={e => setProfile({ ...profile, personalEmail: e.target.value })}
                          placeholder="name@example.com"
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Phone</label>
                        <div className="grid grid-cols-[80px_minmax(0,1fr)] gap-2">
                          <select
                            value={selectedPhoneCountryCode}
                            onChange={(event) => {
                              const nextCode = event.target.value;
                              const nextCountry = COUNTRY_OPTIONS.find((country) => country.code === nextCode);
                              setSelectedPhoneCountryCode(nextCode);
                              setProfile((current: any) => ({
                                ...current,
                                country: current.country || nextCountry?.name || current.country,
                                personalPhone: buildInternationalPhoneNumber(nextCountry?.dialCode || "", profilePhoneLocalNumber),
                              }));
                            }}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                          >
                            {PHONE_COUNTRY_OPTIONS.map((country) => (
                              <option key={country.code} value={country.code}>
                                {country.flag} {country.dialCode}
                              </option>
                            ))}
                          </select>
                          <input
                            type="text"
                            value={profilePhoneLocalNumber}
                            onChange={e => setProfile({
                              ...profile,
                              personalPhone: buildInternationalPhoneNumber(selectedPhoneCountry?.dialCode || "", e.target.value),
                            })}
                            placeholder="7700 900123"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                          />
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                          {selectedPhoneCountry ? `${selectedPhoneCountry.flag} ${selectedPhoneCountry.name} · ${selectedPhoneCountry.dialCode}` : "Choose a country code and enter the local number."}
                        </p>
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Birthday</label>
                        <input
                          type="date"
                          value={profile.birthday || ""}
                          onChange={e => setProfile({...profile, birthday: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
                    <div className="mb-4">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Address</p>
                      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Mailing details used for contracts, HR and contact records.</p>
                    </div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="md:col-span-2">
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Address Line 1</label>
                        <input
                          type="text"
                          value={profileAddressLine1}
                          onChange={e => setProfile({...profile, address: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Address Line 2</label>
                        <input
                          type="text"
                          value={profileAddressLine2}
                          onChange={e => setProfile({...profile, addressLine2: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">City</label>
                        <input
                          type="text"
                          value={profileCity}
                          onChange={e => setProfile({...profile, city: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">State / Province</label>
                        <input
                          type="text"
                          value={profileStateProvince}
                          onChange={e => setProfile({...profile, stateProvince: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">ZIP / Postal Code</label>
                        <input
                          type="text"
                          value={profilePostalCode}
                          onChange={e => setProfile({...profile, postalCode: e.target.value})}
                          className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                        />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Country</label>
                        <div ref={countryComboboxRef} className="relative">
                          <input
                            type="text"
                            value={countryQuery}
                            onFocus={() => setIsCountryMenuOpen(true)}
                            onChange={(event) => {
                              setCountryQuery(event.target.value);
                              setIsCountryMenuOpen(true);
                              if (!event.target.value.trim()) {
                                setProfile((current: any) => ({ ...current, country: "" }));
                              }
                            }}
                            onBlur={() => {
                              window.setTimeout(() => {
                                const exactMatch = COUNTRY_OPTIONS.find(
                                  (country) => country.name.toLowerCase() === countryQuery.trim().toLowerCase(),
                                );
                                if (exactMatch) {
                                  commitCountrySelection(exactMatch);
                                  return;
                                }
                                if (!countryQuery.trim()) {
                                  commitCountrySelection(null);
                                  return;
                                }
                                if (selectedCountryOption) {
                                  setCountryQuery(selectedCountryOption.name);
                                  return;
                                }
                                const bestGuess = filteredCountryOptions[0];
                                if (bestGuess) {
                                  commitCountrySelection(bestGuess);
                                }
                              }, 120);
                            }}
                            placeholder="Start typing a country"
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                          />
                          {isCountryMenuOpen ? (
                            <div className="absolute left-0 right-0 top-[calc(100%+0.4rem)] z-30 max-h-72 overflow-y-auto rounded-2xl border border-slate-200 bg-white p-1.5 shadow-xl dark:border-slate-800 dark:bg-slate-950">
                              <button
                                type="button"
                                onMouseDown={(event) => event.preventDefault()}
                                onClick={() => commitCountrySelection(null)}
                                className="flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm text-slate-600 transition hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-900"
                              >
                                <span className="text-base">∅</span>
                                <span>Select a country</span>
                              </button>
                              {filteredCountryOptions.map((country) => (
                                <button
                                  key={country.code}
                                  type="button"
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => commitCountrySelection(country)}
                                  className={cn(
                                    "flex w-full items-center gap-2 rounded-xl px-3 py-2 text-left text-sm transition",
                                    selectedCountryOption?.code === country.code
                                      ? "bg-[#ffcc01]/10 text-slate-900 dark:bg-[#ffcc01]/15 dark:text-slate-50"
                                      : "text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900",
                                  )}
                                >
                                  <span className="text-base">{country.flag}</span>
                                  <span>{country.name}</span>
                                </button>
                              ))}
                              {!filteredCountryOptions.length ? (
                                <div className="px-3 py-2 text-sm text-slate-500 dark:text-slate-400">
                                  No countries match “{countryQuery}”.
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                        <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                          {selectedCountryOption
                            ? `${selectedCountryOption.flag} ${selectedCountryOption.name}`
                            : "Start typing and choose the best country match to keep addresses consistent."}
                        </p>
                      </div>
                    </div>
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">Notes</label>
                    <textarea
                      value={profile.notes || ""}
                      onChange={e => setProfile({...profile, notes: e.target.value})}
                      rows={5}
                      placeholder="Internal context, preferences, access notes, or other useful staff details."
                      className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-[#ffcc01] focus:ring-2 focus:ring-[#ffcc01]/30 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100"
                    />
                  </section>
                </div>

                <div className="space-y-5 xl:justify-self-end xl:w-full xl:max-w-[420px]">
                  <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/80">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Profile Summary</p>
                        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">A quick sense-check on how complete this record is.</p>
                      </div>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
                        {[profileFirstName, profileLastName, profileWorkEmail, profilePersonalEmail, profilePhone, profileAddressLine1, profileCity, profileCountry]
                          .filter((value) => String(value || "").trim()).length}
                        /8 populated
                      </span>
                    </div>
                    <div className="space-y-3">
                      {[
                        { label: "Display name", value: profileDisplayName || "Not set" },
                        { label: "Work email", value: profileWorkEmail || "Not set" },
                        { label: "Department", value: person.department || profile.department || "Not assigned" },
                        { label: "Role / title", value: person.title || profile.title || "Not assigned" },
                        { label: "Country", value: profileCountry || "Not set" },
                      ].map((item) => (
                        <div key={item.label} className="flex items-center justify-between gap-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-900/70">
                          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500">{item.label}</span>
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">{item.value}</span>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Integrations</h4>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Where this person currently resolves across connected systems.
                        </p>
                      </div>
                      <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 shadow-sm dark:bg-slate-950 dark:text-slate-400">
                        {integrationEntries.filter((entry) => entry.state === "linked").length} linked
                      </span>
                    </div>

                    <div className="mt-4 space-y-2.5">
                      {integrationEntries.map((entry) => (
                        <div
                          key={entry.key}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 dark:border-slate-800 dark:bg-slate-950"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{entry.label}</p>
                              <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{entry.detail || "No link yet"}</p>
                            </div>
                            <span
                              className={cn(
                                "inline-flex shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                                entry.state === "linked"
                                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                                  : "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300"
                              )}
                            >
                              {entry.statusLabel}
                            </span>
                          </div>
                          {entry.lastSyncedAt ? (
                            <p className="mt-1.5 text-[11px] text-slate-400 dark:text-slate-500">
                              Synced {new Date(entry.lastSyncedAt).toLocaleDateString()}
                            </p>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </section>
                </div>
              </div>

            </div>
          )}

          {activeTab === "employment" && (
            <div className="space-y-6 max-w-2xl">
              <div className="border-b border-slate-200 pb-3 dark:border-slate-800">
                <SectionPillHeader icon={Briefcase} title="Employment" />
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-4 dark:border-slate-800 dark:bg-slate-900/60">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Employment History</h4>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Separate contract periods now land here so returning staff can build up a proper employment timeline.
                    </p>
                  </div>
                  <span className="rounded-full bg-white px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 shadow-sm dark:bg-slate-950 dark:text-slate-400">
                    {employmentRecords.length} record{employmentRecords.length === 1 ? "" : "s"}
                  </span>
                </div>
                <div className="mt-4 space-y-3">
                  {loadingEmploymentRecords ? (
                    <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading employment history…
                    </div>
                  ) : employmentRecords.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-slate-200 px-4 py-5 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                      No employment records have been saved for this person yet.
                    </div>
                  ) : (
                    employmentRecords.map((record) => (
                      <div
                        key={record.id}
                        className="rounded-lg border border-slate-200 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-950"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <div className="flex flex-wrap items-center gap-2">
                              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                                {record.contractType || "Employment record"}
                              </p>
                              <span className="inline-flex rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                                {record.source.replace(/_/g, " ")}
                              </span>
                            </div>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {record.startDate || "No start date"}
                              {record.endDate ? ` → ${record.endDate}` : ""}
                              {record.companyName ? ` • ${record.companyName}` : ""}
                            </p>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {[record.jobTitle, record.department].filter(Boolean).join(" • ") || "No title or department"}
                            </p>
                          </div>
                          <div className="text-right text-xs text-slate-500 dark:text-slate-400">
                            {typeof record.salary === "number" ? (
                              <p>
                                {new Intl.NumberFormat("en-GB", {
                                  style: "currency",
                                  currency: "GBP",
                                  maximumFractionDigits: 0,
                                }).format(record.salary)}
                              </p>
                            ) : typeof record.dailyRate === "number" ? (
                              <p>
                                {new Intl.NumberFormat("en-GB", {
                                  style: "currency",
                                  currency: "GBP",
                                  maximumFractionDigits: 0,
                                }).format(record.dailyRate)}{" "}
                                / day
                              </p>
                            ) : (
                              <p>No pay data</p>
                            )}
                            {typeof record.annualLeaveAllowance === "number" ? (
                              <p className="mt-1">{record.annualLeaveAllowance} days leave</p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-950">
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Current Employment Snapshot</h4>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    These fields stay in place for compatibility while the fuller employment-history model beds in.
                  </p>
                </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employee ID</label>
                  <input 
                    type="text" 
                    value={profile.employeeId || ""} 
                    onChange={e => setProfile({...profile, employeeId: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Contract Type</label>
                  <select 
                    value={profileContractType} 
                    onChange={e => setProfile({...profile, contractType: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  >
                    <option value="">Select...</option>
                    {EMPLOYMENT_CONTRACT_OPTIONS.map((option) => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Manager ID</label>
                  <input 
                    type="text" 
                    value={profile.managerId || ""} 
                    onChange={e => setProfile({...profile, managerId: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Leave Allowance (Days)</label>
                  <input 
                    type="number" 
                    value={profileLeaveAllowance ?? ""} 
                    onChange={e => setProfile({
                      ...profile,
                      annualLeaveAllowance: e.target.value === "" ? undefined : parseInt(e.target.value, 10),
                    })}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name</label>
                  <input
                    type="text"
                    value={profile.companyName || ""}
                    onChange={e => setProfile({...profile, companyName: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
              </div>
            </div>
          )}

          {activeTab === "compensation" && (
            <div className="space-y-6 max-w-2xl">
              <div className="border-b border-slate-200 pb-3 dark:border-slate-800">
                <SectionPillHeader icon={DollarSign} title="Compensation" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Annual Salary</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">£</span>
                    <input 
                      type="number" 
                      value={profile.salary || ""} 
                      onChange={e => setProfile({...profile, salary: e.target.value === "" ? undefined : parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" 
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Daily Rate</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">£</span>
                    <input 
                      type="number" 
                      value={profile.dailyRate || ""} 
                      onChange={e => setProfile({...profile, dailyRate: e.target.value === "" ? undefined : parseFloat(e.target.value)})}
                      className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" 
                    />
                  </div>
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Finance uses this explicit daily rate for task costing. We can derive it from annual salary later if needed.
                  </p>
                </div>
              </div>
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50/70 dark:bg-slate-900/60 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Payroll Summary</h4>
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      Reference values captured from payroll uploads. These are kept for audit and operational context.
                    </p>
                  </div>
                  {profile.payrollImportedAt ? (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      Imported {new Date(profile.payrollImportedAt).toLocaleDateString()}
                    </p>
                  ) : null}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Monthly Salary</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">£</span>
                      <input 
                        type="number" 
                        value={profile.monthlySalary || ""} 
                        onChange={e => setProfile({...profile, monthlySalary: e.target.value === "" ? undefined : parseFloat(e.target.value)})}
                        className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employer NI</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">£</span>
                      <input 
                        type="number" 
                        value={profile.employersNi || ""} 
                        onChange={e => setProfile({...profile, employersNi: e.target.value === "" ? undefined : parseFloat(e.target.value)})}
                        className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Employer Pension</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">£</span>
                      <input 
                        type="number" 
                        value={profile.employersPension || ""} 
                        onChange={e => setProfile({...profile, employersPension: e.target.value === "" ? undefined : parseFloat(e.target.value)})}
                        className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" 
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Total Daily Cost</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-slate-400">£</span>
                      <input 
                        type="number" 
                        value={profile.totalDailyCost || ""} 
                        onChange={e => setProfile({...profile, totalDailyCost: e.target.value === "" ? undefined : parseFloat(e.target.value)})}
                        className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded-md text-slate-800 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500" 
                      />
                    </div>
                  </div>
                </div>
                {profile.payrollSource ? (
                  <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                    Source: {profile.payrollSource}
                  </p>
                ) : null}
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Payroll ID</label>
                  <input 
                    type="text" 
                    value={profile.payrollId || ""} 
                    onChange={e => setProfile({...profile, payrollId: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "dates" && (
            <div className="space-y-6 max-w-2xl">
              <div className="border-b border-slate-200 pb-3 dark:border-slate-800">
                <SectionPillHeader icon={Calendar} title="Dates & Reviews" />
              </div>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Contract Start Date</label>
                  <input 
                    type="date" 
                    value={profile.contractStartDate || ""} 
                    onChange={e => setProfile({...profile, contractStartDate: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Contract End Date</label>
                  <input 
                    type="date" 
                    value={profile.contractEndDate || ""} 
                    onChange={e => setProfile({...profile, contractEndDate: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Probation End Date</label>
                  <input 
                    type="date" 
                    value={profile.probationEndDate || ""} 
                    onChange={e => setProfile({...profile, probationEndDate: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Next Review Date</label>
                  <input 
                    type="date" 
                    value={profile.nextReviewDate || ""} 
                    onChange={e => setProfile({...profile, nextReviewDate: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Visa Expiry Date</label>
                  <input 
                    type="date" 
                    value={profile.visaExpiryDate || ""} 
                    onChange={e => setProfile({...profile, visaExpiryDate: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Offer Letter Date</label>
                  <input
                    type="date"
                    value={profile.offerLetterDate || ""}
                    onChange={e => setProfile({...profile, offerLetterDate: e.target.value})}
                    className="w-full px-2 py-1 text-xs bg-white dark:bg-slate-950 border border-slate-300 dark:border-slate-700 rounded text-slate-800 dark:text-slate-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "emergency" && (
            <div className="space-y-6 max-w-2xl">
              <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-2">
                <SectionPillHeader icon={AlertCircle} title="Emergency" />
                <button className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-300 hover:text-indigo-700 dark:hover:text-indigo-200">
                  <Plus className="w-4 h-4" /> Add Contact
                </button>
              </div>
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                No emergency contacts added yet.
              </div>
            </div>
          )}

          {activeTab === "documents" && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-slate-900 p-4 rounded-lg border border-slate-200 dark:border-slate-800 flex items-center justify-between shadow-sm shrink-0">
                <SectionPillHeader
                  icon={FileText}
                  title="Documents"
                  description="Manage employee contracts, letters, and HR files"
                />
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => setShowContractGenerator(true)}
                    className="px-3 py-1.5 rounded-lg transition-colors text-xs font-medium flex items-center gap-2 text-slate-900 bg-[#ffcc01] border border-[#ffcc01] hover:brightness-95"
                    title="Generate a contract package for this person."
                  >
                    <FilePlus2 className="w-3.5 h-3.5" />
                    Generate Contract
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const targetUrl = documentsState?.currentFolderId
                        ? `https://drive.google.com/drive/folders/${documentsState.currentFolderId}`
                        : profile.driveFolderId
                          ? `https://drive.google.com/drive/folders/${profile.driveFolderId}`
                          : "https://drive.google.com/drive/my-drive";
                      window.open(targetUrl, "_blank", "noopener,noreferrer");
                    }}
                    className={cn(
                      "px-3 py-1.5 rounded-lg transition-colors text-xs font-medium flex items-center gap-2",
                      profile.driveFolderId
                        ? "bg-transparent border border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-300"
                        : "border border-amber-200 dark:border-amber-900/50 text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30 hover:bg-amber-100 dark:hover:bg-amber-950/40 shadow-sm"
                    )}
                    >
                      <img
                        src="https://www.gstatic.com/images/branding/product/1x/drive_48dp.png"
                      alt="Drive"
                      className={cn("w-3.5 h-3.5", profile.driveFolderId ? "" : "grayscale opacity-70")}
                        referrerPolicy="no-referrer"
                      />
                    {profile.driveFolderId
                      ? `${getDisplayDriveFolderName(documentsState?.rootFolderName || profile.driveFolderName || person.name, person.name)} · Google Drive Linked`
                      : "No Drive Folder Linked"}
                    {profile.driveFolderId ? <ExternalLink className="w-3 h-3 text-slate-400 dark:text-slate-500" /> : null}
                  </button>
                </div>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(event) => handleUploadDocument(event.target.files?.[0] || null)}
              />

              {profile.driveFolderId ? (
                <div className="space-y-4 xl:max-w-[75%]">
                  <div className="rounded-xl border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Contract History</h4>
                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                          Generated contracts are tracked here so each employment package remains visible even after it lands in Drive.
                        </p>
                      </div>
                      <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 dark:bg-slate-800 dark:text-slate-300">
                        {contractHistory.length} record{contractHistory.length === 1 ? "" : "s"}
                      </span>
                    </div>
                    <div className="mt-4 space-y-3">
                      {loadingContractHistory ? (
                        <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Loading contract history…
                        </div>
                      ) : contractHistory.length === 0 ? (
                        <div className="rounded-lg border border-dashed border-slate-200 px-4 py-5 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                          No contract records have been saved for this person yet.
                        </div>
                      ) : (
                        contractHistory.map((record) => {
                          const generatedLabel = record.generatedAt ? new Date(record.generatedAt).toLocaleString() : "Unknown";
                          return (
                            <div
                              key={record.id}
                              className="rounded-lg border border-slate-200 px-4 py-3 dark:border-slate-800"
                            >
                              <div className="flex flex-wrap items-start justify-between gap-3">
                                <div>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{record.contractType}</p>
                                    <span
                                      className={cn(
                                        "inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                                        record.generationStatus === "success"
                                          ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300"
                                          : record.generationStatus === "partial_failure"
                                            ? "bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300"
                                            : "bg-rose-100 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300"
                                      )}
                                    >
                                      {record.generationStatus.replace("_", " ")}
                                    </span>
                                  </div>
                                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    {record.startDate}
                                    {record.endDate ? ` → ${record.endDate}` : ""}
                                    {record.companyName ? ` • ${record.companyName}` : ""}
                                  </p>
                                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    Generated {generatedLabel}
                                    {typeof record.salary === "number"
                                      ? ` • ${new Intl.NumberFormat("en-GB", {
                                          style: "currency",
                                          currency: "GBP",
                                          maximumFractionDigits: 0,
                                        }).format(record.salary)}`
                                      : ""}
                                  </p>
                                </div>
                                <div className="flex flex-wrap items-center gap-2">
                                  {record.contractDocId ? (
                                    <button
                                      type="button"
                                      onClick={() => window.open(`https://docs.google.com/document/d/${record.contractDocId}/edit`, "_blank", "noopener,noreferrer")}
                                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                    >
                                      <FileText className="h-3.5 w-3.5" />
                                      Open Contract
                                    </button>
                                  ) : null}
                                  {record.contractInstanceFolderId ? (
                                    <button
                                      type="button"
                                      onClick={() => window.open(`https://drive.google.com/drive/folders/${record.contractInstanceFolderId}`, "_blank", "noopener,noreferrer")}
                                      className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 px-2.5 py-1.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
                                    >
                                      <Folder className="h-3.5 w-3.5" />
                                      Open Folder
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                              {record.warnings?.length ? (
                                <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                                  {record.warnings.join(" • ")}
                                </div>
                              ) : null}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col gap-3 mb-1">
                    <div className="flex flex-wrap items-center gap-2 w-full">
                        <div className="relative min-w-[160px] flex-[1_1_16rem]">
                          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                          <input
                            type="text"
                            placeholder="Search Drive..."
                            value={documentSearch}
                            onChange={(event) => setDocumentSearch(event.target.value)}
                            className="w-full pl-8 pr-3 py-1.5 text-[11px] border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={handleCreateManagementFolders}
                          disabled={creatingManagementFolders}
                          className="flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                          title="Create the standard HR management folders inside this person's documents root."
                        >
                          {creatingManagementFolders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Folder className="w-3.5 h-3.5" />}
                          Create Management Folders
                        </button>
                        <button
                          type="button"
                          onClick={handleMoveContractFolders}
                          disabled={movingContractFolders}
                          className="flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                          title="Create or reuse the Contracts folder and move all contract folders into it."
                        >
                          {movingContractFolders ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Folder className="w-3.5 h-3.5" />}
                          Move Contract Folders
                        </button>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingDocument || !documentsState?.currentFolderId}
                          className="flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          {uploadingDocument ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                          Upload to Drive
                        </button>
                        <button
                          type="button"
                          onClick={handleCreateSubfolder}
                          disabled={creatingSubfolder || !documentsState?.currentFolderId}
                          className="flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                        >
                          {creatingSubfolder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderPlus className="w-3.5 h-3.5" />}
                          New Folder
                        </button>
                      </div>
                    </div>

                  <div
                    className={cn(
                      "relative bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[300px] flex flex-col transition-colors",
                      documentDropActive ? "border-[#ffcc01] bg-amber-50/30 dark:bg-amber-950/10" : ""
                    )}
                    onDragEnter={handleDocumentDragEnter}
                    onDragOver={handleDocumentDragOver}
                    onDragLeave={handleDocumentDragLeave}
                    onDrop={handleDocumentDrop}
                  >
                    {documentDropActive ? (
                      <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-amber-50/85 text-center dark:bg-slate-900/85">
                        <div className="rounded-xl border border-dashed border-[#ffcc01] bg-white/90 px-6 py-4 shadow-sm dark:bg-slate-950/90">
                          <Upload className="mx-auto h-6 w-6 text-[#d4a500]" />
                          <p className="mt-2 text-sm font-semibold text-slate-900 dark:text-slate-100">Drop file to upload</p>
                          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                            It will be added to {getDisplayDriveFolderName(documentsState?.currentFolderName || "the current folder", "the current folder")}.
                          </p>
                        </div>
                      </div>
                    ) : null}
                    <div className="px-3 py-1.5 bg-slate-50/50 dark:bg-slate-800/70 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2 overflow-x-auto text-[11px] font-medium text-slate-500 dark:text-slate-400">
                      <button
                        type="button"
                        onClick={() => profile.driveFolderId && handleOpenFolder(profile.driveFolderId)}
                        className="hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors flex items-center gap-1.5 shrink-0"
                      >
                        <Folder className="w-3.5 h-3.5" />
                        {getDisplayDriveFolderName(documentsState?.rootFolderName || profile.driveFolderName, "Person Root")}
                      </button>
                      {(documentsState?.breadcrumbs || [])
                        .filter((crumb) => crumb.id !== profile.driveFolderId)
                        .map((crumb) => (
                          <React.Fragment key={crumb.id}>
                            <ChevronRight className="w-3 h-3 text-slate-400" />
                            <button
                              type="button"
                              onClick={() => handleOpenFolder(crumb.id)}
                              className={cn(
                                "hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors truncate max-w-[280px] flex items-center gap-1.5 shrink-0",
                                documentsState?.currentFolderId === crumb.id ? "text-slate-800 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"
                              )}
                            >
                              <Folder className="w-3 h-3" />
                              {getDisplayDriveFolderName(crumb.name, "Folder")}
                            </button>
                          </React.Fragment>
                        ))}
                    </div>

                    <div className="flex-1 overflow-auto">
                      {loadingDocs ? (
                        <div className="h-full flex items-center justify-center p-12">
                          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
                        </div>
                      ) : filteredDocuments.length === 0 ? (
                        <div className="h-full flex flex-col items-center justify-center p-12 text-center">
                          <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                            <Folder className="w-6 h-6 text-slate-300 dark:text-slate-500" />
                          </div>
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">
                            {documentSearch.trim() ? "No matching documents" : "No files or folders yet"}
                          </h4>
                          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                            {documentSearch.trim()
                              ? "Try a different search term or clear the search box."
                              : "This folder is ready for contracts, handbooks, and other HR documents."}
                          </p>
                        </div>
                      ) : (
                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                          {filteredDocuments.map((doc) => {
                            const isFolder = doc.mimeType === "application/vnd.google-apps.folder";
                            return (
                              <div key={doc.id} className="group flex items-center justify-between gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/70 transition-colors">
                                <button
                                  type="button"
                                  onClick={() => isFolder ? handleOpenFolder(doc.id) : window.open(doc.webViewLink, "_blank", "noopener,noreferrer")}
                                  className="flex min-w-0 flex-1 items-center gap-3 text-left transition-colors hover:text-indigo-600"
                                >
                                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-slate-100 dark:bg-slate-800">
                                    {getDocumentIcon(doc)}
                                  </div>
                                  <div className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900 dark:text-slate-100">{doc.name}</div>
                                </button>
                                <div className="flex shrink-0 items-center gap-2 opacity-0 transition-all group-hover:opacity-100 focus-within:opacity-100">
                                  <button
                                    type="button"
                                    onClick={() => openMoveDialog(doc)}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1.5 text-[11px] font-medium text-slate-500 dark:text-slate-300 transition-all hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-100"
                                  >
                                    <ArrowRightLeft className="h-3.5 w-3.5" />
                                    Move
                                  </button>
                                </div>
                                <div className="hidden shrink-0 text-[11px] text-slate-500 dark:text-slate-400 md:block">
                                  Modified {doc.modifiedTime ? new Date(doc.modifiedTime).toLocaleDateString() : "—"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      onClick={handleCreateFolder}
                      disabled={creatingFolder}
                      className="flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-medium text-white bg-[#ffcc01] border border-[#ffcc01] rounded-lg hover:brightness-95 transition-colors disabled:opacity-50"
                    >
                      {creatingFolder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                      Create Documents Folder
                    </button>
                    <button
                      type="button"
                      onClick={handleLinkExistingFolder}
                      disabled={linkingExistingFolder}
                      className="flex items-center gap-1.5 whitespace-nowrap px-2.5 py-1.5 text-[11px] font-medium text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                    >
                      {linkingExistingFolder ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ExternalLink className="w-3.5 h-3.5" />}
                      Link Folder
                    </button>
                  </div>

                  <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden min-h-[320px] flex flex-col">
                    <div className="flex-1 flex flex-col items-center justify-center p-12 text-center">
                      <div className="w-12 h-12 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-3">
                        <FileText className="w-6 h-6 text-slate-300 dark:text-slate-500" />
                      </div>
                      <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100 mb-1">No Drive Folder</h4>
                      <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto">
                        Create or link a Google Drive folder to store this employee&apos;s contracts and HR documents inside the People area.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === "permissions" && (
            <div className="space-y-6 max-w-2xl">
              <div className="border-b border-slate-200 pb-3 dark:border-slate-800">
                <SectionPillHeader icon={Shield} title="Permissions" />
              </div>
              <UserAccessEditor userRecord={person} onSaveAccess={handleSaveAccess} />
            </div>
          )}

          {activeTab === "audit" && (
            <div className="space-y-6 max-w-2xl">
              <div className="border-b border-slate-200 pb-3 dark:border-slate-800">
                <SectionPillHeader icon={History} title="Audit" />
              </div>
              <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                No recent activity.
              </div>
            </div>
          )}
        </div>
    </div>
  );
}
